import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/PageHero";
import { MultiStepQuoteForm } from "@/components/MultiStepQuoteForm";
import { TrustStrip } from "@/components/TrustStrip";
import { FAQAccordion } from "@/components/FAQAccordion";
import { SectionHeading } from "@/components/SectionHeading";
import { SITE, TRUST_POINTS } from "@/lib/site";
import { OBJECTION_FAQS } from "@/lib/faqs";
import { buildFaqPage, buildBreadcrumbs } from "@/components/SchemaJsonLd";
import { Phone, Mail, MapPin } from "lucide-react";
import heroImg from "@/assets/hero-garage.jpg";

export const Route = createFileRoute("/quote")({
  head: () => ({
    meta: [
      { title: "Get a Free Garage Floor Coating Quote | MLC Epoxy Floors" },
      { name: "description", content: "Request a free, fixed-price quote for your garage floor or commercial concrete coating project. Serving the entire Calgary Region from Airdrie, AB." },
      { property: "og:title", content: "Get a Free Quote | MLC Epoxy Floors" },
      { property: "og:description", content: "Free in-person estimates throughout the Calgary region. No-pressure quotes — usually within one business day." },
      { property: "og:url", content: "/quote" },
    ],
    links: [{ rel: "canonical", href: "/quote" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(buildFaqPage(OBJECTION_FAQS)) },
      { type: "application/ld+json", children: JSON.stringify(buildBreadcrumbs([
        { name: "Home", href: "/" },
        { name: "Get a Quote", href: "/quote" },
      ])) },
    ],
  }),
  component: QuotePage,
});

function QuotePage() {
  return (
    <>
      <PageHero eyebrow="Free Estimate" title="Get Your Free Garage Floor Quote" body="Tell us about your project and a member of our team will reach out within one business day to schedule your free in-person assessment." image={heroImg} showCTA={false} />
      <TrustStrip />
      <section className="container-x py-20 grid gap-12 lg:grid-cols-[2fr_1fr]">
        <div className="rounded-md border border-border bg-ivory p-7 md:p-8 shadow-2xl">
          <MultiStepQuoteForm />
        </div>
        <aside className="space-y-6">
          <div className="rounded-sm border border-border bg-charcoal p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-copper">What to expect</p>
            <ul className="mt-4 space-y-2 text-sm text-silver">
              {TRUST_POINTS.map((p) => <li key={p}>• {p}</li>)}
            </ul>
          </div>
          <div className="rounded-sm border border-border bg-charcoal p-6 space-y-3 text-sm text-silver">
            <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-copper" /> <a href={SITE.phoneHref} className="hover:text-copper">{SITE.phone}</a></p>
            <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-copper" /> <a href={`mailto:${SITE.email}`} className="hover:text-copper">{SITE.email}</a></p>
            <p className="flex items-start gap-2"><MapPin className="h-4 w-4 text-copper mt-0.5" /> {SITE.address.street}, {SITE.address.city}, {SITE.address.region} {SITE.address.postal}</p>
          </div>
        </aside>
      </section>

      <section className="section-light">
        <div className="container-x py-20 md:py-28">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-12">
            <SectionHeading
              eyebrow="Before You Decide"
              title="The Questions Every Homeowner Asks Us."
              body="The real concerns we hear on every quote call — answered up front, no sales spin."
            />
            <div>
              <FAQAccordion items={OBJECTION_FAQS} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
